/**
 * Experiments (WALLIYO-0132, #132).
 *
 * Rollout control. A customer feature on for some share of people, ramped in
 * steps, each step its own audit row — because "who took it to fifty" is the
 * question a post-mortem asks.
 *
 * The ramp is deliberately buttons and not a slider: 10, 25, 50, 100 are
 * decisions, and a slider invites 37% for no reason anybody can defend later.
 */

import type { Experiment } from "@wallyo/contracts/console/experiments";
import { SETTINGS } from "@wallyo/contracts/settings";
import { useState } from "react";

import { createExperiment, listExperiments, moveExperiment } from "../../api/console-api";
import { useApi } from "../../api/context";
import { customerFeatureName, t } from "../../i18n";
import { useAlerts } from "../../shell/alerts";
import { PageTop } from "../../shell/PageTop";
import { LoadFailed, Loading, useLoad } from "../config-parts";
import { ReasonDialogView, useIntentWrite } from "../team/TeamDialogs";

/** The steps worth taking. A slider invites a share nobody can defend. */
const RAMP = [10, 25, 50] as const;

const CUSTOMER_FEATURES = SETTINGS.filter(
  (setting) => setting.kind === "feature_enablement" && setting.settingId.endsWith("_USER_FEATURE_ENABLEMENT"),
);

const TONE: Record<Experiment["status"], string> = {
  draft: "",
  running: "ok",
  stopped: "bad",
  shipped: "info",
};

export function ExperimentsScreen() {
  const { client } = useApi();
  const alerts = useAlerts();
  const [reads, setReads] = useState(0);
  const [creating, setCreating] = useState(false);
  const load = useLoad(() => listExperiments(client), String(reads));
  const experiments = load.state.data?.experiments ?? [];
  const reload = () => setReads((count) => count + 1);

  const move = (experiment: Experiment, patch: { status?: Experiment["status"]; percent?: number }, why: string) => {
    void moveExperiment(client, experiment.experimentId, { ...patch, version: experiment.version, reason: why }).then(
      ({ experiment: saved }) => {
        alerts.ok(t("experiments.moved", { reference: saved.reference, percent: String(saved.percent) }));
        reload();
      },
      (error: unknown) => alerts.problem(error),
    );
  };

  return (
    <>
      <PageTop title={t("experiments.title")} sub={t("experiments.sub")} />

      <div className="hr-search">
        <div className="grow" />
        <button className="btn primary" type="button" onClick={() => setCreating(true)}>
          {t("experiments.start")}
        </button>
      </div>

      {load.state.status === "failed" && !load.state.data ? (
        <LoadFailed
          message={t("experiments.loadFailed")}
          retry={t("hr.retry")}
          error={load.state.error}
          onRetry={load.reload}
        />
      ) : null}
      {!load.state.data && load.state.status === "loading" ? <Loading text={t("hr.loading")} /> : null}

      {load.state.data ? (
        <section className="card">
          {experiments.length === 0 ? (
            <div className="empty">{t("experiments.none")}</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("experiments.col.name")}</th>
                    <th>{t("experiments.col.feature")}</th>
                    <th className="num">{t("experiments.col.share")}</th>
                    <th>{t("experiments.col.status")}</th>
                    <th>{t("experiments.col.ramp")}</th>
                  </tr>
                </thead>
                <tbody>
                  {experiments.map((experiment) => (
                    <tr key={experiment.experimentId}>
                      <td>
                        <strong>{experiment.name}</strong>
                        <div className="foot mono">{experiment.reference}</div>
                        {experiment.metric ? <div className="foot">{experiment.metric}</div> : null}
                      </td>
                      <td className="foot">{customerFeatureName(experiment.settingId as never)}</td>
                      <td className="num mono">{experiment.percent}%</td>
                      <td>
                        <span className={`pill ${TONE[experiment.status]}`}>
                          {t(`experiments.status.${experiment.status}` as never)}
                        </span>
                      </td>
                      <td>
                        <div className="btns">
                          {experiment.status !== "shipped" && experiment.status !== "stopped"
                            ? RAMP.filter((percent) => percent > experiment.percent).map((percent) => (
                                <button
                                  key={percent}
                                  className="btn quiet small"
                                  type="button"
                                  onClick={() =>
                                    move(experiment, { status: "running", percent }, `Ramp to ${percent}%`)
                                  }
                                >
                                  {percent}%
                                </button>
                              ))
                            : null}
                          {experiment.status === "running" ? (
                            <>
                              <button
                                className="btn small"
                                type="button"
                                onClick={() => move(experiment, { status: "shipped" }, "Ship it")}
                              >
                                {t("experiments.ship")}
                              </button>
                              <button
                                className="btn quiet small"
                                type="button"
                                onClick={() => move(experiment, { status: "stopped" }, "Stop it")}
                              >
                                {t("experiments.stop")}
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="foot">{t("experiments.sticky")}</p>
        </section>
      ) : null}

      {creating ? (
        <CreateDialog
          onClose={() => setCreating(false)}
          onDone={(experiment) => {
            setCreating(false);
            alerts.ok(t("experiments.created", { reference: experiment.reference }));
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function CreateDialog({ onClose, onDone }: { onClose: () => void; onDone: (experiment: Experiment) => void }) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [name, setName] = useState("");
  const [settingId, setSettingId] = useState("");
  const [metric, setMetric] = useState("");
  const [reason, setReason] = useState("");

  return (
    <ReasonDialogView
      title={t("experiments.startTitle")}
      body={<p className="meta">{t("experiments.startBody")}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("experiments.startSubmit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={onClose}
      onSubmit={() =>
        void write.run(
          settingId === "" ? t("experiments.featureNeeded") : null,
          (key) =>
            createExperiment(
              client,
              { name: name.trim(), settingId, metric: metric.trim() || null, reason: reason.trim() },
              { idempotencyKey: key },
            ),
          ({ experiment }) => onDone(experiment),
        )
      }
    >
      <div className="field">
        <label htmlFor="experiment-name">{t("experiments.field.name")}</label>
        <input
          id="experiment-name"
          value={name}
          onChange={(event) => {
            write.edited();
            setName(event.target.value);
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="experiment-feature">{t("experiments.field.feature")}</label>
        <select id="experiment-feature" value={settingId} onChange={(event) => setSettingId(event.target.value)}>
          <option value="">{t("experiments.pickFeature")}</option>
          {CUSTOMER_FEATURES.map((setting) => (
            <option key={setting.settingId} value={setting.settingId}>
              {customerFeatureName(setting.settingId as never)}
            </option>
          ))}
        </select>
        <span className="meta">{t("experiments.field.featureHint")}</span>
      </div>
      <div className="field">
        <label htmlFor="experiment-metric">{t("experiments.field.metric")}</label>
        <input id="experiment-metric" value={metric} onChange={(event) => setMetric(event.target.value)} />
        <span className="meta">{t("experiments.field.metricHint")}</span>
      </div>
    </ReasonDialogView>
  );
}
